import { useState, type ChangeEvent } from "react";
import { TextField } from "./Field.js";
import { copy } from "../shared/copy.js";

interface Props {
  value: string;
  onChange: (next: string) => void;
  error?: string;
}

// Formats +7 (XXX) XXX-XX-XX as the user types RU digits.
function formatRuDisplay(digits: string): string {
  const d = digits.startsWith("7") ? digits.slice(1) : digits;
  const parts = ["+7"];
  if (d.length > 0) parts.push(` (${d.slice(0, 3)}`);
  if (d.length >= 3) parts.push(`) ${d.slice(3, 6)}`);
  if (d.length >= 6) parts.push(`-${d.slice(6, 8)}`);
  if (d.length >= 8) parts.push(`-${d.slice(8, 10)}`);
  return parts.join("");
}

export function PhoneField({ value, onChange, error }: Props) {
  // Local mode toggle: default is +7 mask, but a parent from a different
  // country can flip to free-form entry and type any + number.
  const [foreign, setForeign] = useState(false);

  if (foreign) {
    return (
      <>
        <TextField
          label={copy.s3.phone}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+…"
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            // Keep leading + and digits, cap at 16 (E.164 max).
            const cleaned = ("+" + e.target.value.replace(/[^\d]/g, "")).slice(0, 16);
            onChange(cleaned);
          }}
          error={error ?? ""}
        />
        <button
          type="button"
          onClick={() => {
            setForeign(false);
            onChange("");
          }}
          style={{
            background: "none",
            border: "none",
            color: "var(--c-muted)",
            fontSize: "var(--fs-14)",
            padding: 0,
            marginTop: "-4px",
            cursor: "pointer",
            textDecoration: "underline",
            textAlign: "left",
            fontFamily: "inherit",
          }}
        >
          Российский номер (+7)
        </button>
      </>
    );
  }

  // RU-mask flow. Always propagate the partial "+7XXXX…" string so the
  // input re-renders as the user types; final validation (/^\+7\d{10}$/)
  // happens at submit time in the parent form.
  const digits = value.replace(/\D/g, "").slice(0, 11);
  const display = digits.length > 0 ? formatRuDisplay(digits) : "";

  return (
    <>
      <TextField
        label={copy.s3.phone}
        type="tel"
        inputMode="numeric"
        autoComplete="tel"
        placeholder="+7 (___) ___-__-__"
        value={display}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const nextDigits = e.target.value.replace(/\D/g, "").slice(0, 11);
          const normalised = nextDigits.startsWith("7")
            ? nextDigits.slice(1)
            : nextDigits;
          const eOne = normalised.length === 0 ? "" : `+7${normalised}`;
          onChange(eOne);
        }}
        error={error ?? ""}
      />
      <button
        type="button"
        onClick={() => {
          setForeign(true);
          onChange("");
        }}
        style={{
          background: "none",
          border: "none",
          color: "var(--c-muted)",
          fontSize: "var(--fs-14)",
          padding: 0,
          marginTop: "-4px",
          cursor: "pointer",
          textDecoration: "underline",
          textAlign: "left",
          fontFamily: "inherit",
        }}
      >
        Другая страна (не +7)
      </button>
    </>
  );
}
